import {
  ConnectorCapability,
  hasCapability,
  type ConnectorCapability as Capability,
} from './ConnectorCapabilities'
import {
  ConnectorCapabilityError,
  ConnectorNotImplementedError,
  ConnectorStateError,
} from './ConnectorErrors'
import { connectorEvents } from './ConnectorEvents'
import { ConnectorStatus } from './ConnectorTypes'
import type {
  ConnectorConnectOptions,
  ConnectorHealthResult,
  ConnectorMeta,
  ConnectorPriceResult,
  ConnectorProductRef,
  ConnectorProductResult,
  ConnectorSearchOptions,
  ConnectorStockResult,
  IConnector,
} from './ConnectorTypes'

export interface BaseConnectorOptions {
  meta: ConnectorMeta
  capabilities?: readonly Capability[]
}

/**
 * BaseConnector — default SDK implementation.
 * Supplier connectors must extend this class and override provider-specific methods.
 * Login / HTTP are not implemented here; Session Manager owns sessions elsewhere.
 */
export abstract class BaseConnector implements IConnector {
  readonly meta: ConnectorMeta
  readonly capabilities: readonly Capability[]

  private status: ConnectorStatus = ConnectorStatus.DISCONNECTED
  private sessionId: string | null = null

  constructor(options: BaseConnectorOptions) {
    this.meta = options.meta
    this.capabilities = options.capabilities ?? [
      ConnectorCapability.LOGIN,
      ConnectorCapability.SEARCH,
      ConnectorCapability.PRICE,
      ConnectorCapability.STOCK,
    ]
  }

  getStatus(): ConnectorStatus {
    return this.status
  }

  protected setStatus(next: ConnectorStatus): void {
    const from = this.status
    if (from === next) return
    this.status = next
    connectorEvents.emit('status_changed', {
      connectorId: this.meta.id,
      from,
      to: next,
    })
  }

  protected getSessionId(): string | null {
    return this.sessionId
  }

  protected setSessionId(sessionId: string | null): void {
    this.sessionId = sessionId
  }

  protected requireCapability(capability: Capability): void {
    if (!hasCapability(this.capabilities, capability)) {
      throw new ConnectorCapabilityError(String(this.meta.id), capability)
    }
  }

  protected requireConnected(): void {
    if (this.status === ConnectorStatus.EXPIRED) {
      throw new ConnectorStateError(String(this.meta.id), 'Sessão expirada — chame refreshSession()/connect()')
    }
    if (this.status !== ConnectorStatus.CONNECTED) {
      throw new ConnectorStateError(
        String(this.meta.id),
        `Conector não conectado (status=${this.status}). Chame connect() antes.`,
      )
    }
  }

  /**
   * Default connect: status machine + hook. Subclasses override `onConnect`.
   * Does not perform real login.
   */
  async connect(options: ConnectorConnectOptions = {}): Promise<void> {
    this.requireCapability(ConnectorCapability.LOGIN)
    connectorEvents.emit('connect_start', { connectorId: this.meta.id })
    this.setStatus(ConnectorStatus.CONNECTING)
    try {
      await this.onConnect(options)
      if (options.sessionId) this.setSessionId(options.sessionId)
      this.setStatus(ConnectorStatus.CONNECTED)
      connectorEvents.emit('connect_success', { connectorId: this.meta.id })
    } catch (error) {
      this.setStatus(ConnectorStatus.ERROR)
      const message = error instanceof Error ? error.message : String(error)
      connectorEvents.emit('connect_error', { connectorId: this.meta.id, error: message })
      connectorEvents.emit('error', {
        connectorId: this.meta.id,
        message,
        code: 'CONNECT_FAILED',
      })
      throw error
    }
  }

  async disconnect(): Promise<void> {
    await this.onDisconnect()
    this.setSessionId(null)
    this.setStatus(ConnectorStatus.DISCONNECTED)
    connectorEvents.emit('disconnect', { connectorId: this.meta.id })
  }

  async testConnection(): Promise<boolean> {
    try {
      const result = await this.health()
      return result.healthy
    } catch {
      return false
    }
  }

  async searchProduct(
    ref: ConnectorProductRef,
    options?: ConnectorSearchOptions,
  ): Promise<ConnectorProductResult | null> {
    this.requireCapability(ConnectorCapability.SEARCH)
    this.requireConnected()
    const results = await this.onSearchProducts(
      ref.query ?? ref.sku ?? ref.ean ?? ref.mpn ?? '',
      { ...options, limit: options?.limit ?? 1 },
      ref,
    )
    const first = results[0] ?? null
    connectorEvents.emit('search', {
      connectorId: this.meta.id,
      query: ref.query ?? ref.sku ?? '',
      resultCount: first ? 1 : 0,
    })
    return first
  }

  async searchProducts(
    query: string,
    options?: ConnectorSearchOptions,
  ): Promise<ConnectorProductResult[]> {
    this.requireCapability(ConnectorCapability.SEARCH)
    this.requireConnected()
    const results = await this.onSearchProducts(query, options)
    connectorEvents.emit('search', {
      connectorId: this.meta.id,
      query,
      resultCount: results.length,
    })
    return results
  }

  async getStock(sku: string): Promise<ConnectorStockResult> {
    this.requireCapability(ConnectorCapability.STOCK)
    this.requireConnected()
    return this.onGetStock(sku)
  }

  async getPrice(sku: string): Promise<ConnectorPriceResult> {
    this.requireCapability(ConnectorCapability.PRICE)
    this.requireConnected()
    return this.onGetPrice(sku)
  }

  /**
   * Default refresh: marks EXPIRED → CONNECTING → hook → CONNECTED.
   * Wire Session Manager inside supplier overrides / future SDK helpers.
   */
  async refreshSession(): Promise<void> {
    this.requireCapability(ConnectorCapability.LOGIN)
    connectorEvents.emit('session_refresh', {
      connectorId: this.meta.id,
      sessionId: this.sessionId ?? undefined,
    })
    this.setStatus(ConnectorStatus.CONNECTING)
    try {
      await this.onRefreshSession()
      this.setStatus(ConnectorStatus.CONNECTED)
    } catch (error) {
      this.setStatus(ConnectorStatus.EXPIRED)
      throw error
    }
  }

  async health(): Promise<ConnectorHealthResult> {
    const started = Date.now()
    try {
      const details = await this.onHealth()
      const healthy =
        this.status === ConnectorStatus.CONNECTED || this.status === ConnectorStatus.CONNECTING
      const result: ConnectorHealthResult = {
        status: this.status,
        healthy: details.healthy ?? healthy,
        checkedAt: new Date().toISOString(),
        latencyMs: Date.now() - started,
        message: details.message,
        details: details.details,
      }
      connectorEvents.emit('health', { connectorId: this.meta.id, healthy: result.healthy })
      return result
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      connectorEvents.emit('health', { connectorId: this.meta.id, healthy: false })
      return {
        status: this.status,
        healthy: false,
        checkedAt: new Date().toISOString(),
        latencyMs: Date.now() - started,
        message,
      }
    }
  }

  /** Override in supplier connectors — no real login in SDK. */
  protected async onConnect(_options: ConnectorConnectOptions): Promise<void> {
    throw new ConnectorNotImplementedError(String(this.meta.id), 'onConnect')
  }

  protected async onDisconnect(): Promise<void> {
    // default: nothing
  }

  protected async onSearchProducts(
    _query: string,
    _options?: ConnectorSearchOptions,
    _ref?: ConnectorProductRef,
  ): Promise<ConnectorProductResult[]> {
    throw new ConnectorNotImplementedError(String(this.meta.id), 'onSearchProducts')
  }

  protected async onGetStock(_sku: string): Promise<ConnectorStockResult> {
    throw new ConnectorNotImplementedError(String(this.meta.id), 'onGetStock')
  }

  protected async onGetPrice(_sku: string): Promise<ConnectorPriceResult> {
    throw new ConnectorNotImplementedError(String(this.meta.id), 'onGetPrice')
  }

  protected async onRefreshSession(): Promise<void> {
    throw new ConnectorNotImplementedError(String(this.meta.id), 'onRefreshSession')
  }

  protected async onHealth(): Promise<{ healthy?: boolean; message?: string; details?: Record<string, unknown> }> {
    return {
      healthy: this.status === ConnectorStatus.CONNECTED,
      message: 'Health padrão do BaseConnector (sem ping remoto)',
    }
  }
}
